import type {
  Event,
  Locality,
  ApiResponse,
  BackendEvent,
  BackendEventDetail,
} from "@/types";
import type { IEventService } from "../types";
import { legacyHttpClient } from "@services/http/legacyHttpClient";
import {
  mapBackendEventToEvent,
  mapBackendEventDetailToEventAndLocalities,
} from "@shared/utils/enrichment";
/** Production event service — calls the legacy Java backend API. */
export const realEventService: IEventService = {
  async getEvents(): Promise<ApiResponse<Event[]>> {
    try {
      const backendEvents =
        await legacyHttpClient.get<BackendEvent[]>("/api/web/events");
      const events = backendEvents.map(mapBackendEventToEvent);
      return { success: true, data: events };
    } catch (error) {
      return {
        success: false,
        data: [],
        message:
          error instanceof Error ? error.message : "Error al cargar eventos",
      };
    }
  },

  async getEventLocalities(
    eventIdentifier: string,
  ): Promise<ApiResponse<{ event: Event; localities: Locality[] }>> {
    try {
      const identifier = eventIdentifier.trim();

      // Resolver SIEMPRE a un id numérico y usar find_by_id_event (endpoint correcto para id).
      // Antes se llamaba find_event?name={id} para ids -> busca por nombre con un id, nunca encuentra.
      let numericId: string | null = /^\d+$/.test(identifier) ? identifier : null;

      if (!numericId) {
        const events =
          await legacyHttpClient.get<BackendEvent[]>("/api/web/events");
        // Comparación normalizada: ignora diferencias de guiones/puntos entre el slug
        // de la URL y el de la BD (ej. "gualaceo-s-c-vs-macara" vs "gualaceo-sc-vs-macara").
        const norm = (s?: string | null) =>
          (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const target = events.find(
          (e) =>
            e.sku === identifier ||
            e.slug === identifier ||
            norm(e.slug) === norm(identifier) ||
            norm(e.sku) === norm(identifier),
        );
        if (target) numericId = String(target.id);
      }

      // Con id: buscar por id (trae las localidades inline via getByRefId, state=0).
      // Sin id resuelto (slug no está en la lista): fallback por nombre.
      const detail = numericId
        ? await legacyHttpClient.get<BackendEventDetail>(
            "/api/web/find_by_id_event",
            { id: numericId },
          )
        : await legacyHttpClient.get<BackendEventDetail>(
            "/api/web/find_event",
            { name: identifier },
          );

      const { event, localities } =
        mapBackendEventDetailToEventAndLocalities(detail);

      return { success: true, data: { event, localities } };
    } catch (error) {
      return {
        success: false,
        data: { event: {} as Event, localities: [] },
        message:
          error instanceof Error
            ? error.message
            : "Error al cargar localidades",
      };
    }
  },

};
